<script setup lang="ts">
import { ref, computed } from 'vue'
import { useFormStore } from '@/stores/form'

const emit = defineEmits<{
  (e: 'submit', value: number): void
}>()

const formStore = useFormStore()

const sliderValue = ref(formStore.data.debtAmountRaw ?? 62) // Default ~$20k

const MIN = 0
const MAX = 380

const formattedValue = computed(() => {
  if (sliderValue.value === 0) return 'Less than $5,000'
  if (sliderValue.value >= MAX) return 'More than $100,000'
  const amount = 5000 + (sliderValue.value * 250)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
})

const numericValue = computed(() => {
  if (sliderValue.value === 0) return 0
  if (sliderValue.value >= MAX) return 101000
  return 5000 + (sliderValue.value * 250)
})

const progressPercent = computed(() => {
  return ((sliderValue.value - MIN) / (MAX - MIN)) * 100
})

function handleSubmit() {
  formStore.setField('debtAmountRaw', sliderValue.value)
  formStore.setField('debtAmount', numericValue.value)
  emit('submit', numericValue.value)
}
</script>

<template>
  <div class="w-full animate-slide-up">
    <div class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mx-1">
      <!-- Value display -->
      <div class="text-center mb-6">
        <p class="text-3xl font-bold text-primary">
          {{ formattedValue }}
        </p>
      </div>

      <!-- Slider -->
      <div class="relative py-4 px-1">
        <input
          type="range"
          :min="MIN"
          :max="MAX"
          v-model.number="sliderValue"
          class="w-full h-2 rounded-full cursor-pointer"
          :style="{
            background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${progressPercent}%, #e5e7eb ${progressPercent}%, #e5e7eb 100%)`
          }"
        />

        <!-- Labels -->
        <div class="flex justify-between mt-3 text-xs text-gray-500">
          <span>$5,000</span>
          <span>$100,000+</span>
        </div>
      </div>

      <!-- Submit button -->
      <button
        @click="handleSubmit"
        class="w-full mt-4 py-4 bg-primary text-white rounded-full font-semibold text-base shadow-lg shadow-primary/30 hover:bg-primary-dark active:scale-[0.98] transition-all"
      >
        Continue
        <svg class="inline w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
</template>
